/**
 * Auth Store
 * Global authentication state management
 */

import { create } from 'zustand';
import { User, sendOTP as sendOTPService, verifyOTP as verifyOTPService, logout as logoutService, initializeAuth } from '@/lib/auth';
import { logger } from '@/lib/logger';
import { BuyerProfile } from '@/lib/constants';

interface AuthState {
  user: User | null;
  buyer: BuyerProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  sendOTP: (phone: string) => Promise<void>;
  verifyOTP: (phone: string, otp: string) => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  buyer: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  sendOTP: async (phone: string) => {
    set({ isLoading: true, error: null });
    try {
      await sendOTPService(phone);
      set({ isLoading: false });
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to send OTP';
      set({ isLoading: false, error: errorMessage });
      throw error;
    }
  },

  verifyOTP: async (phone: string, otp: string) => {
    set({ isLoading: true, error: null });
    try {
      const result = await verifyOTPService(phone, otp);
      set({
        user: result.user,
        buyer: result.buyer || null,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      logger.info('User authenticated', { userId: result.user.id });
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to verify OTP';
      set({ isLoading: false, error: errorMessage });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await logoutService();
      set({
        user: null,
        buyer: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      logger.info('User logged out');
    } catch (error) {
      const errorMessage = (error as Error).message || 'Logout failed';
      set({ isLoading: false, error: errorMessage });
      logger.error('Logout failed', error as Error);
    }
  },

  initialize: () => {
    const { isAuthenticated, user } = initializeAuth();
    set({
      isAuthenticated,
      user,
      isLoading: false,
    });
    if (isAuthenticated && user) {
      logger.info('Auth state restored', { userId: user.id });
    }
  },

  clearError: () => set({ error: null }),
}));

















